import React, { useEffect } from 'react'
import { Link, useLocation } from 'react-router-dom'

/**
 * Pentabay-branded 404 - shown for any React route that doesn't match
 * one of the loaded HTML pages. Self-contained so it doesn't depend on
 * the source HTML's design tokens being injected (those only mount with
 * HtmlPage).
 */
export default function NotFound() {
  const { pathname } = useLocation()

  // While this page is mounted, give the document a 404 vibe so the
  // dropdown / observers (which carry over) don't try to translate
  // anything that doesn't belong to a source page.
  // Title/meta/canonical are NOT set here - src/RouteSEO.jsx owns all of
  // that for every route including this one (see isKnownRoute() /
  // NOT_FOUND_SEO there), so there is exactly one place that can set them
  // and no race between the two components over document.title.
  useEffect(() => {
    document.body.setAttribute('data-pb-route', '404')
    return () => {
      document.body.removeAttribute('data-pb-route')
    }
  }, [])

  return (
    <div className="pb-404">
      <style>{styles}</style>

      {/* Background aurae - same brand palette as the main site */}
      <div className="pb-404-aurae" aria-hidden="true">
        <span className="pb-404-aura pb-404-aura-blue" />
        <span className="pb-404-aura pb-404-aura-red" />
        <span className="pb-404-grain" />
      </div>

      <header className="pb-404-nav">
        <Link to="/" className="pb-404-brand" aria-label="Pentabay home">
          {/* Show the dark-text wordmark on light backgrounds, the
              light-text wordmark on dark - matches the source site's
              own logo-swap pattern. */}
          <img
            src="/assets/pentabay-logo-dark.webp"
            alt="Pentabay"
            className="pb-404-logo pb-404-logo-onlight"
          />
          <img
            src="/assets/pentabay-logo-light.webp"
            alt="Pentabay"
            className="pb-404-logo pb-404-logo-ondark"
          />
        </Link>
        <span className="pb-404-eyebrow" aria-hidden="true">
          STATUS, 404, ROUTE NOT FOUND
        </span>
      </header>

      <main className="pb-404-main">
        <div className="pb-404-card">
          <div className="pb-404-code" aria-hidden="true">
            <span className="pb-404-digit">4</span>
            <span className="pb-404-zero">
              <span className="pb-404-zero-inner" />
            </span>
            <span className="pb-404-digit">4</span>
          </div>

          <p className="pb-404-trace" data-pb-skip-translate>
            <span className="pb-404-prompt">$</span> resolve route{' '}
            <span className="pb-404-path">{pathname || '/'}</span>{' '}
            <span className="pb-404-fail">- no match</span>
          </p>

          <h1 className="pb-404-title">
            This route hasn’t shipped yet.
          </h1>

          <p className="pb-404-lead">
            The path you tried doesn’t live on this domain. It may have
            moved, been renamed, or never existed. Pick a known route to
            keep going.
          </p>

          <nav className="pb-404-routes" aria-label="Suggested routes">
            <Link to="/" className="pb-404-cta pb-404-cta-primary">
              <span>Go home</span>
              <span aria-hidden="true">→</span>
            </Link>
            <a href="/#/services" className="pb-404-cta">Services</a>
            <a href="/#/trust" className="pb-404-cta">Trust</a>
            <a href="/#/compliance" className="pb-404-cta">Compliance</a>
          </nav>

          <ul className="pb-404-meta" data-pb-skip-translate>
            <li>
              <span className="k">Request</span>
              <span className="v">GET {pathname || '/'}</span>
            </li>
            <li>
              <span className="k">Response</span>
              <span className="v">404, NOT_FOUND</span>
            </li>
            <li>
              <span className="k">Engineered</span>
              <span className="v">in Canada, since 2018</span>
            </li>
          </ul>
        </div>
      </main>
    </div>
  )
}

const styles = `
.pb-404 {
  --bg:       #060A14;
  --panel:    #08101F;
  --panel-2:  #0E1426;
  --hair:     #1A2240;
  /* Pure endpoints on both themes, matching the site ramp in Pentabay.html:
     --fg and --muted were #F7F8FB / #98A2B3 here, which is the half-white pair
     the rest of the site dropped. */
  --fg:       #FFFFFF;
  --muted:    #FFFFFF;
  --blue:     #0F4FBA;
  --blue-2:   #1E5FD4;
  --red:      #C8102E;

  position: fixed; inset: 0;
  display: flex; flex-direction: column;
  background: var(--bg);
  color: var(--fg);
  font-family: 'Open Sans', system-ui, -apple-system, Segoe UI, sans-serif;
  overflow: hidden;
  isolation: isolate;
  min-height: 0;
}
:root[data-theme="light"] .pb-404 {
  --bg: #F7F8FB;
  --panel: #FFFFFF;
  --panel-2: #F1F4FB;
  --hair: #E3E7EE;
  --fg: #000000;
  --muted: #000000;
}

.pb-404-aurae { position: absolute; inset: 0; z-index: 0; pointer-events: none; }
.pb-404-aura {
  position: absolute;
  width: 60vmax; height: 60vmax;
  border-radius: 9999px;
  filter: blur(80px);
  opacity: 0.45;
}
.pb-404-aura-blue {
  top: -20vmax; left: -10vmax;
  background: radial-gradient(closest-side, var(--blue) 0%, transparent 70%);
}
.pb-404-aura-red {
  bottom: -25vmax; right: -15vmax;
  background: radial-gradient(closest-side, var(--red) 0%, transparent 70%);
  opacity: 0.30;
}
.pb-404-grain {
  position: absolute; inset: 0;
  background-image:
    radial-gradient(rgba(255,255,255,0.05) 1px, transparent 1px),
    radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px);
  background-size: 3px 3px, 5px 5px;
  background-position: 0 0, 1.5px 1.5px;
  opacity: 0.5;
}
:root[data-theme="light"] .pb-404-grain {
  background-image:
    radial-gradient(rgba(11,16,32,0.05) 1px, transparent 1px),
    radial-gradient(rgba(11,16,32,0.04) 1px, transparent 1px);
}

.pb-404-nav {
  position: relative; z-index: 2;
  display: flex; align-items: center; justify-content: space-between;
  gap: 16px;
  padding: 20px clamp(16px, 4vw, 48px);
}
.pb-404-brand {
  display: inline-flex; align-items: center; gap: 10px;
  color: inherit; text-decoration: none;
}
.pb-404-logo { display: block; height: 32px; width: auto; }
@media (min-width: 1024px) { .pb-404-logo { height: 36px; } }
/* Default (no explicit theme = our dark surface) → light wordmark */
.pb-404-logo-onlight { display: none; }
.pb-404-logo-ondark  { display: block; }
:root[data-theme="light"] .pb-404-logo-onlight { display: block; }
:root[data-theme="light"] .pb-404-logo-ondark  { display: none; }
.pb-404-eyebrow {
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10px;
  letter-spacing: 0.18em;
  color: var(--muted);
  text-transform: uppercase;
}

.pb-404-main {
  position: relative; z-index: 1;
  flex: 1 1 auto;
  display: grid; place-items: center;
  padding: clamp(12px, 3vw, 40px);
  overflow-y: auto;
  min-height: 0;
}
.pb-404-card {
  position: relative;
  width: 100%; max-width: 680px;
  background: linear-gradient(180deg, var(--panel) 0%, var(--panel-2) 100%);
  border: 1px solid var(--hair);
  border-radius: 22px;
  padding: clamp(22px, 3vw, 44px);
  text-align: center;
  box-shadow: 0 30px 90px -30px rgba(15, 79, 186, 0.35),
              0 0 0 1px rgba(255,255,255,0.02) inset;
}

.pb-404-code {
  display: flex; align-items: center; justify-content: center;
  gap: clamp(10px, 1.6vw, 22px);
  margin-bottom: 16px;
  user-select: none;
}
.pb-404-digit {
  font-family: 'Poppins', system-ui, sans-serif;
  font-weight: 800;
  font-size: clamp(64px, 9.5vw, 128px);
  line-height: 1;
  letter-spacing: -0.04em;
  background: linear-gradient(180deg, var(--fg) 0%, var(--muted) 100%);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
}
.pb-404-zero {
  position: relative;
  width:  clamp(58px, 8.4vw, 116px);
  height: clamp(58px, 8.4vw, 116px);
  border-radius: 9999px;
  border: clamp(6px, 1.1vw, 13px) solid transparent;
  background:
    linear-gradient(var(--panel-2), var(--panel-2)) padding-box,
    linear-gradient(135deg, var(--blue) 0%, var(--red) 100%) border-box;
  box-shadow: 0 12px 40px -8px rgba(15, 79, 186, 0.45);
}
.pb-404-zero-inner {
  position: absolute;
  inset: 50%;
  width: 14%; height: 14%;
  transform: translate(-50%, -50%);
  border-radius: 9999px;
  background: var(--red);
  box-shadow: 0 0 0 6px rgba(200, 16, 46, 0.20);
}

.pb-404-trace {
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 12px;
  color: var(--muted);
  margin: 0 0 12px;
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.pb-404-prompt { color: var(--blue); margin-right: 6px; }
.pb-404-path   { color: var(--fg); }
.pb-404-fail   { color: var(--red); }

.pb-404-title {
  font-size: clamp(22px, 3.2vw, 40px);
  line-height: 1.08;
  letter-spacing: -0.02em;
  font-weight: 700;
  margin: 0 0 10px;
}
.pb-404-lead {
  font-size: clamp(14px, 1.1vw, 16px);
  line-height: 1.5;
  color: var(--muted);
  margin: 0 auto 20px;
  max-width: 52ch;
}

.pb-404-routes {
  display: flex; flex-wrap: wrap; gap: 8px;
  justify-content: center;
  margin-bottom: 20px;
}
.pb-404-cta {
  display: inline-flex; align-items: center; gap: 8px;
  height: 40px; padding: 0 16px;
  border-radius: 9999px;
  font-weight: 600; font-size: 13px;
  background: transparent;
  color: var(--fg);
  border: 1px solid var(--hair);
  text-decoration: none;
  transition: transform .2s ease, border-color .2s, background .2s, color .2s;
}
.pb-404-cta:hover {
  border-color: var(--blue);
  color: var(--fg);
  transform: translateY(-1px);
}
.pb-404-cta-primary {
  background: var(--blue);
  border-color: transparent;
  color: #fff;
  box-shadow: 0 10px 30px -10px rgba(15, 79, 186, 0.60);
}
.pb-404-cta-primary:hover {
  background: var(--blue-2);
  color: #fff;
}

.pb-404-meta {
  list-style: none; padding: 0; margin: 0 auto;
  display: inline-grid; gap: 4px;
  border-top: 1px solid var(--hair);
  padding-top: 14px;
  font-family: 'Open Sans', ui-monospace, monospace;
  font-size: 10.5px;
  color: var(--muted);
  text-align: left;
}
.pb-404-meta li {
  display: flex; gap: 14px; align-items: baseline;
  justify-content: flex-start;
}
.pb-404-meta .k {
  flex: 0 0 100px;
  letter-spacing: 0.16em; text-transform: uppercase;
  color: var(--muted);
}
.pb-404-meta .v {
  color: var(--fg);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}

@media (max-width: 560px) {
  .pb-404-eyebrow { display: none; }
  .pb-404-meta .k { flex: 0 0 80px; }
}
/* On short viewports drop the bottom meta strip rather than letting
   the card spill past the fold. The main area is also scrollable, so
   even if it does spill the user can still reach the CTAs. */
@media (max-height: 680px) {
  .pb-404-meta { display: none; }
  .pb-404-card { padding: 20px; }
  .pb-404-digit { font-size: clamp(54px, 7.6vw, 96px); }
  .pb-404-zero {
    width: clamp(48px, 6.8vw, 88px);
    height: clamp(48px, 6.8vw, 88px);
    border-width: clamp(5px, 0.9vw, 10px);
  }
  .pb-404-title { font-size: clamp(20px, 2.8vw, 32px); }
}
`
